import { useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { toast } from 'sonner';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';

const FollowerMessageEditor = ({
  value,
  onChange,
  onSave,
  onEdit,
  isEditing,
  followerId,
}) => {
  const textareaRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [copied, setCopied] = useState(false);

  // Place cursor at end of text when entering edit mode
  useEffect(() => {
    if (isEditing && textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.focus();
      textarea.setSelectionRange(textarea.value.length, textarea.value.length);
    }
  }, [isEditing]);

  const handleCopy = async () => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error('Failed to copy to clipboard');
    }
  };

  if (!isEditing) {
    return (
      <div
        className={cn(
          'relative min-h-[150px] cursor-pointer rounded-lg bg-muted/50 p-3 transition-all duration-200 hover:bg-muted/80',
          'whitespace-pre-wrap border border-muted-foreground/20 hover:ring-2 hover:ring-primary/20'
        )}
        onClick={() => onEdit(followerId)}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        {isHovering && value && (
          <Button
            variant='ghost'
            size='icon'
            className='absolute right-2 top-2 z-10 h-6 w-6 bg-background/80 hover:bg-background'
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            title='Copy to clipboard'
          >
            {copied ? (
              <Check className='h-3 w-3 text-green-600' />
            ) : (
              <Copy className='h-3 w-3' />
            )}
          </Button>
        )}
        <p className='line-clamp-6 min-h-[120px] text-xs leading-normal text-foreground'>
          {value || 'No personalized message'}
        </p>
        <p className='mt-2 text-xs text-muted-foreground'>Click to edit</p>
      </div>
    );
  }

  return (
    <div className='space-y-2'>
      <Textarea
        ref={textareaRef}
        value={value}
        onChange={onChange}
        onBlur={() => onSave(followerId)} // Auto-save on blur
        placeholder='Enter personalized opening message...'
        className={cn(
          'custom-scrollbar relative min-h-[150px] resize-none overflow-y-auto rounded-lg border border-muted-foreground/20 bg-muted/50 text-xs transition-all duration-300 ease-in-out focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:ring-offset-0',
          'max-h-60'
        )}
        onInput={(e) => {
          const target = e.target;
          target.style.height = 'auto';
          target.style.height = `${target.scrollHeight}px`;
        }}
      />
    </div>
  );
};

export default FollowerMessageEditor;
